import React, { useState } from 'react';
import { Plus, Search, Edit, Trash2, Tag } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import Modal from '../../components/ui/Modal';
import CategoryForm from './CategoryForm';
import apiService from '../../services/apiService';

const CategoriesTab = ({ categorias, onCategoriesChange }) => {
    const { loading, setLoading, setError, clearError } = useApp();

    const [filteredCategorias, setFilteredCategorias] = useState(categorias);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedCategoria, setSelectedCategoria] = useState(null);

    React.useEffect(() => {
        filterCategorias();
    }, [categorias, searchQuery]);

    const filterCategorias = () => {
        if (!searchQuery.trim()) {
            setFilteredCategorias(categorias);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = categorias.filter(categoria =>
            categoria.nombre?.toLowerCase().includes(query)
        );

        setFilteredCategorias(filtered);
    };

    const handleCreateCategoria = async (categoriaData) => {
        try {
            const result = await apiService.crearCategoria(categoriaData);

            if (result.success) {
                setShowCreateModal(false);
                onCategoriesChange();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al crear categoría');
        }
    };

    const handleEditCategoria = async (categoriaData) => {
        try {
            const result = await apiService.actualizarCategoria({
                id: selectedCategoria.id,
                ...categoriaData
            });

            if (result.success) {
                setShowEditModal(false);
                setSelectedCategoria(null);
                onCategoriesChange();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al actualizar categoría');
        }
    };

    const handleToggleActivo = async (categoria) => {
        try {
            const result = await apiService.actualizarCategoria({
                id: categoria.id,
                nombre: categoria.nombre,
                activo: categoria.activo ? 0 : 1
            });

            if (result.success) {
                onCategoriesChange();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al actualizar estado de la categoría');
        }
    };

    const handleDeleteCategoria = async (id, nombre) => {
        if (!window.confirm(`¿Estás seguro de que quieres eliminar la categoría "${nombre}"?`)) {
            return;
        }

        try {
            const result = await apiService.eliminarCategoria(id);

            if (result.success) {
                onCategoriesChange();
            } else {
                setError(result.message);
            }
        } catch (error) {
            setError('Error al eliminar categoría');
        }
    };

    return (
        <>
            {/* Controles */}
            <div className="product-controls">
                <div className="search-filters">
                    <div className="search-container">
                        <Input
                            placeholder="Buscar por nombre..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            icon={<Search size={20} />}
                            className="search-input"
                        />
                    </div>
                </div>

                <div className="controls-actions">
                    <Button
                        onClick={() => setShowCreateModal(true)}
                        disabled={loading}
                    >

                        Nueva Categoría
                    </Button>
                </div>
            </div>

            {/* Tabla de categorías */}
            <div className="products-table-container">
                {loading && (
                    <div className="loading-state">
                        <div className="loading-spinner"></div>
                        <p>Cargando categorías...</p>
                    </div>
                )}

                {!loading && filteredCategorias.length === 0 && (
                    <div className="empty-state">
                        <Tag size={48} />
                        <h3>No se encontraron categorías</h3>
                        <p>
                            {searchQuery
                                ? 'No hay categorías que coincidan con tu búsqueda'
                                : 'No hay categorías registradas'
                            }
                        </p>
                        {!searchQuery && (
                            <Button
                                onClick={() => setShowCreateModal(true)}
                                className="empty-action-btn"
                            >

                                Crear primera categoría
                            </Button>
                        )}
                    </div>
                )}

                {!loading && filteredCategorias.length > 0 && (
                    <table className="products-table categories-table">
                        <thead>
                            <tr>
                                <th>Nombre</th>
                                <th>Estado</th>
                                <th>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredCategorias.map((categoria) => (
                                <tr key={categoria.id}>
                                    <td>
                                        <div className="category-info">
                                            <div className="category-name">
                                                <Tag size={14} />
                                                {categoria.nombre}
                                            </div>
                                        </div>
                                    </td>

                                    <td>
                                        <label className="toggle-switch">
                                            <input
                                                type="checkbox"
                                                checked={categoria.activo}
                                                onChange={() => handleToggleActivo(categoria)}
                                                disabled={loading}
                                            />
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </td>

                                    <td className="actions">
                                        <div className="actions-compact">
                                            <button
                                                className="action-btn-compact"
                                                onClick={() => {
                                                    setSelectedCategoria(categoria);
                                                    setShowEditModal(true);
                                                }}
                                                title="Editar"
                                            >
                                                <Edit size={14} />
                                            </button>

                                            <button
                                                className="action-btn-compact delete"
                                                onClick={() => handleDeleteCategoria(categoria.id, categoria.nombre)}
                                                title="Eliminar"
                                                disabled={categoria.productos_count > 0}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modales */}
            {showCreateModal && (
                <Modal
                    title="Crear Nueva Categoría"
                    onClose={() => setShowCreateModal(false)}
                    size="medium"
                >
                    <CategoryForm
                        onSubmit={handleCreateCategoria}
                        onCancel={() => setShowCreateModal(false)}
                        isLoading={loading}
                    />
                </Modal>
            )}

            {showEditModal && selectedCategoria && (
                <Modal
                    title="Editar Categoría"
                    onClose={() => {
                        setShowEditModal(false);
                        setSelectedCategoria(null);
                    }}
                    size="medium"
                >
                    <CategoryForm
                        categoria={selectedCategoria}
                        onSubmit={handleEditCategoria}
                        onCancel={() => {
                            setShowEditModal(false);
                            setSelectedCategoria(null);
                        }}
                        isLoading={loading}
                        isEdit={true}
                    />
                </Modal>
            )}
        </>
    );
};

export default CategoriesTab;